import collection from '../data/trivia.json';

export interface TriviaStory {
  id: string;
  title: string;
  lead: string;
  details: string;
  sources: { name: string; url: string }[];
}

export const triviaStories: readonly TriviaStory[] = collection.items;
export const TRIVIA_HISTORY_KEY = 'przedrejsem:trivia-history:v1';
export const TRIVIA_PREFERENCE_KEY = 'przedrejsem:remember-trivia:v1';
type HistoryStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Draw from unseen IDs; new entries join the current round automatically. */
export function drawTrivia(
  stories: readonly TriviaStory[],
  storage?: HistoryStorage,
  random = Math.random,
): TriviaStory | undefined {
  if (!stories.length) return undefined;
  const ids = new Set(stories.map((story) => story.id));
  let seen: string[] = [];
  try {
    const history = JSON.parse(storage?.getItem(TRIVIA_HISTORY_KEY) || 'null');
    if (history?.version === 1 && Array.isArray(history.seen)) {
      seen = [...new Set<string>(history.seen.filter((id: unknown) => typeof id === 'string' && ids.has(id)))];
    }
  } catch {
    // A damaged or unavailable browser store must not hide the panel.
  }

  let available = stories.filter((story) => !seen.includes(story.id));
  if (!available.length) {
    // Start a fresh round without repeating the very last story back to back.
    const lastId = seen.at(-1);
    available = stories.length > 1 ? stories.filter((story) => story.id !== lastId) : [...stories];
    seen = [];
  }
  const story = available[Math.floor(random() * available.length)];
  try {
    storage?.setItem(TRIVIA_HISTORY_KEY, JSON.stringify({ version: 1, seen: [...seen, story.id] }));
  } catch {
    // Display still works when storage is blocked or its quota is exhausted.
  }
  return story;
}

let pageStory: TriviaStory | null | undefined;

export function remembersTrivia() {
  try { return window.localStorage.getItem(TRIVIA_PREFERENCE_KEY) === 'true'; } catch { return false; }
}
export function rememberTrivia(enabled: boolean): boolean {
  try {
    if (enabled) {
      window.localStorage.setItem(TRIVIA_PREFERENCE_KEY,'true');
      if (pageStory) window.localStorage.setItem(TRIVIA_HISTORY_KEY,JSON.stringify({ version:1,seen:[pageStory.id] }));
    } else {
      window.localStorage.removeItem(TRIVIA_PREFERENCE_KEY);
      window.localStorage.removeItem(TRIVIA_HISTORY_KEY);
    }
    return enabled;
  } catch { return remembersTrivia(); }
}

export function getTriviaForPageLoad(): TriviaStory | null {
  if (typeof window === 'undefined') return null;
  // Keep one draw for this document, including StrictMode and SPA navigation.
  if (pageStory !== undefined) return pageStory;
  let storage: HistoryStorage | undefined;
  try {
    if (remembersTrivia()) storage = window.localStorage;
    else window.localStorage.removeItem(TRIVIA_HISTORY_KEY);
  } catch { /* Storage can be disabled. */ }
  pageStory = drawTrivia(triviaStories, storage) ?? null;
  return pageStory;
}
